import { useEffect, useState, type FormEvent } from 'react';
import { AlertCircle, ChevronRight, Save, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import {
  useAdminAdvisorEditor,
  useSaveAdminAdvisor,
} from '../../hooks/useAdminAdvisors';
import type { AdvisorEditorFormData } from './advisorEditor.types';
import { AdvisorEditorTabs } from './AdvisorEditorTabs';
import {
  advisorDetailToForm,
  advisorFormToPayload,
} from './utils/advisorEditorTransformers';
import { SendWelcomeEmailButton } from '../SendWelcomeEmailButton';
import { LinkProvisionalContactButton } from '../manual-referrals/LinkProvisionalContactButton';
import { ManualReferralProfileButton } from '../manual-referrals/ManualReferralProfileButton';

type AdvisorEditorPageProps = {
  advisorId: string;
  onBack: () => void;
};

export const AdvisorEditorPage = ({ advisorId, onBack }: AdvisorEditorPageProps) => {
  const { showToast } = useToast();
  const { data: advisor, isLoading, error, refetch } = useAdminAdvisorEditor(advisorId);
  const saveAdvisor = useSaveAdminAdvisor();
  const [formData, setFormData] = useState<AdvisorEditorFormData | null>(null);
  const [initialFormData, setInitialFormData] = useState<AdvisorEditorFormData | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof AdvisorEditorFormData, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  // A photo still uploading has no URL yet; saving then would persist the old value.
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  useEffect(() => {
    if (!advisor) return;
    const nextFormData = advisorDetailToForm(advisor);
    setFormData(nextFormData);
    setInitialFormData(nextFormData);
  }, [advisor]);

  const isDirty =
    !!formData &&
    !!initialFormData &&
    JSON.stringify(formData) !== JSON.stringify(initialFormData);
  const hasBlockingValidationError =
    !!formData && Object.keys(computeAdvisorEditorErrors(formData)).length > 0;

  const handleChange = <K extends keyof AdvisorEditorFormData>(
    field: K,
    value: AdvisorEditorFormData[K],
  ) => {
    setFormData((current) => {
      if (!current) return current;
      const next = { ...current, [field]: value };
      const nextErrors = computeAdvisorEditorErrors(next);
      setErrors((prev) => ({ ...prev, [field]: nextErrors[field] }));
      return next;
    });
    setServerError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!advisor || !formData || isPhotoUploading || !validateForm(formData, setErrors)) return;

    try {
      const savedAdvisor = await saveAdvisor.mutateAsync({
        id: advisor.id,
        payload: advisorFormToPayload(formData),
      });
      const savedFormData = advisorDetailToForm(savedAdvisor);
      setFormData(savedFormData);
      setInitialFormData(savedFormData);
      showToast('Financial Advisor profile saved successfully.', 'success');
    } catch (saveError) {
      setServerError(
        saveError instanceof Error
          ? saveError.message
          : 'Unable to save Financial Advisor profile.',
      );
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="mx-auto max-w-5xl rounded-lg border border-red-200 bg-white p-8 text-center">
          <AlertCircle className="mx-auto mb-4 h-10 w-10 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900">
            {isNotFound(error) ? 'Financial Advisor not found' : 'Unable to load advisor'}
          </h2>
          <p className="mt-2 text-gray-600">
            {isNotFound(error)
              ? 'Return to the list and select another advisor.'
              : 'Try loading the editor again.'}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button type="button" variant="outline" onClick={onBack}>
              Back to Advisors
            </Button>
            {!isNotFound(error) && (
              <Button type="button" onClick={() => refetch()}>
                Try Again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <AdvisorEditorSkeleton />;
  }

  if (!advisor) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="mx-auto max-w-5xl rounded-lg border border-red-200 bg-white p-8 text-center">
          <AlertCircle className="mx-auto mb-4 h-10 w-10 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900">Unable to load advisor</h2>
          <p className="mt-2 text-gray-600">Try loading the editor again.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Button type="button" variant="outline" onClick={onBack}>
              Back to Advisors
            </Button>
            <Button type="button" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!formData || !initialFormData) {
    return <AdvisorEditorSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-6 py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
              <button type="button" className="hover:text-[#026857]" onClick={onBack}>
                Financial Advisors
              </button>
              <ChevronRight className="h-4 w-4" />
              <span>Edit</span>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Financial Advisor Editor
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <SendWelcomeEmailButton
              userId={advisor.user.id}
              userRole="FINANCIAL_ADVISOR"
              userEmail={advisor.user.email}
            />
            <ManualReferralProfileButton
              referredEntity={{
                id: advisor.user.id,
                kind: 'USER',
                label: advisor.companyName,
                email: advisor.user.email,
                role: 'FINANCIAL_ADVISOR',
                organizationName: advisor.companyName,
              }}
            />
            <LinkProvisionalContactButton
              userId={advisor.user.id}
              role="FINANCIAL_ADVISOR"
              accountLabel={advisor.companyName}
            />
            <Button type="button" variant="outline" onClick={onBack} disabled={saveAdvisor.isPending}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saveAdvisor.isPending || isPhotoUploading || hasBlockingValidationError}
            >
              <Save className="mr-2 h-4 w-4" />
              {saveAdvisor.isPending ? 'Saving...' : isPhotoUploading ? 'Uploading photo...' : 'Save'}
            </Button>
          </div>
        </div>

        {serverError && (
          <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {serverError}
          </div>
        )}

        <AdvisorEditorTabs
          advisor={advisor}
          formData={formData}
          errors={errors}
          isDirty={isDirty}
          onChange={handleChange}
          onPhotoUploadingChange={setIsPhotoUploading}
        />
      </div>
    </form>
  );
};

const ADVISOR_BIOGRAPHY_MAX_LENGTH = 1000;
const STATE_CODE_PATTERN = /^[A-Z]{2}$/i;

export const computeAdvisorEditorErrors = (
  formData: AdvisorEditorFormData,
): Partial<Record<keyof AdvisorEditorFormData, string>> => {
  const nextErrors: Partial<Record<keyof AdvisorEditorFormData, string>> = {};

  if (!formData.firstName.trim()) {
    nextErrors.firstName = 'First name is required';
  }
  if (!formData.email.trim()) {
    nextErrors.email = 'Email is required';
  }
  if (!formData.companyName.trim()) {
    nextErrors.companyName = 'Company name is required';
  }
  // Feature 071: firm-size segment is required on any save.
  if (!formData.firmSizeSegment) {
    nextErrors.firmSizeSegment = 'Firm size segment is required';
  }
  if (formData.advisorBiography.length > ADVISOR_BIOGRAPHY_MAX_LENGTH) {
    nextErrors.advisorBiography = `Biography must be ${ADVISOR_BIOGRAPHY_MAX_LENGTH} characters or fewer`;
  }
  const trimmedState = formData.addressState.trim();
  if (trimmedState.length > 0 && !STATE_CODE_PATTERN.test(trimmedState)) {
    nextErrors.addressState = 'State must be a two-letter US state code';
  }

  return nextErrors;
};

const validateForm = (
  formData: AdvisorEditorFormData,
  setErrors: (errors: Partial<Record<keyof AdvisorEditorFormData, string>>) => void,
) => {
  const nextErrors = computeAdvisorEditorErrors(formData);
  setErrors(nextErrors);
  return Object.keys(nextErrors).length === 0;
};

const isNotFound = (error: unknown) => {
  const maybeError = error as { statusCode?: number; response?: { status?: number } } | null;
  return maybeError?.statusCode === 404 || maybeError?.response?.status === 404;
};

const AdvisorEditorSkeleton = () => (
  <div className="min-h-screen bg-background p-6" aria-label="Loading advisor editor">
    <div className="mx-auto max-w-6xl space-y-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-80 w-full" />
    </div>
  </div>
);
